<template>
  <el-upload
    class="upload-img-single"
    action="#"
    :accept="accept"
    :show-file-list="false"
    :before-upload="beforeUpload"
    :http-request="uploadRequest"
    :style="{
      '--width': width,
      '--height': height,
      '--align': uploadBtnAlign,
      '--backColor': backColor
    }"
  >
    <div v-if="imageUrl" class="upload-img-container">
      <div class="image-wrapper">
        <el-image class="upload-img" :src="fullImageUrl" fit="contain" />
      </div>
      <img :src="delImg" class="del-icon" @click.stop="handleDelImg" />
    </div>
    <div v-if="!imageUrl" class="upload-icon">
      <img :src="uploadIcon" class="avatar" />
      <p>点击上传</p>
    </div>
    <template #tip>
      <div
        class="upload-img-tip"
        :style="{
          '--tipSize': tipSize,
          'margin-top': !imageUrl ? '6px' : '',
        }"
      >
        {{ tip }}
      </div>
    </template>
  </el-upload>
</template>

<script  setup>
import {computed, ref, watch,defineExpose, defineProps, defineEmits, } from 'vue'
import {ElMessage} from 'element-plus'
import { uploadSingleFile } from '@/utils/ObsUpload'
import uploadIcon from '@/assets/images/plus.png'
import delImg from '@/assets/images/delIcon.png'
import { verifyType, verifySize, compressImg } from './verify.js'
let prop = defineProps({
  value: {
    type: String,
    default: '',
  },
  accept: {
    type: String,
    default: '.jpeg,.png,.jpg',
  },
  size: {
    //单位 kb
    type: Number,
    default: 5 * 1024,
  },
  ratio: {
    type: String,
    default: '4:2',
  },
  tip: {
    type: String,
    default: '只能上传jpg/png文件，且不超过5MB',
  },
  needCompress: {
    type: Boolean,
    default: true,
  },
  width: {
    //上传组件宽度
    type: String,
    default: '200px',
  },
  height: {
    type: String,
    default: '150px',
  },
  tipSize: {
    //提示文字大小
    type: String,
    default: '14px',
  },
  inTextSize: {
    //上传框内 点击上传 文字大小
    type: String,
    default: '14px',
  },
  avatarWidth: {
    //图标大小
    type: String,
    default: '30px',
  },
  uploadBtnAlign: {
    //上传选择框对齐方式 left 、center、right
    type: String,
    default: 'left',
  },
  fileTypeText: {
    type: String,
    default: '文件',
  },
  backColor: {
    //背景颜色
    type: String,
    default: '#fff',
  },
  isCompress: {
    //是否压缩
    type: Boolean,
    default: true,
  },
})
const emit = defineEmits(['update:value'])
const imageUrl = ref(prop.value)

const fullImageUrl = computed(function () {
  if(imageUrl.value.startsWith('https://')){
    return imageUrl.value
  }
  return imageUrl.value
})
// 上传前文件限制校验
const beforeUpload = (rawFile) => {
  return new Promise((resolve, reject) => {
    const processUpload = async () => {
      try {
        const { name, size: fileSize } = rawFile
        verifyType(name, prop.accept) // 校验文件类型
        verifySize(fileSize, prop.size, prop.fileTypeText) // 校验文件大小

        let resUrl
        if(prop.isCompress) {
          resUrl = await compressImg(rawFile) //压缩图片
        } else {
          resUrl = {
            file: rawFile,
            origin: rawFile,
            beforeSize: rawFile.size,
            afterSize: rawFile.size
          }
        }
        resolve(resUrl.file)
      } catch (error) {
        ElMessage.warning(error.message || '上传失败！')
        reject(error)
        return false // 明确阻止上传
      }
    }
    // 立即执行异步处理
    processUpload()
        .then(() => {
          // 如果需要额外的成功处理可以在这里执行
        })
        .catch((error) => {
          // 错误已在上方处理，这里保持空或添加日志
          console.error('Upload error:', error)
        })
  })
}

// 上传文件请求
const uploadRequest = async (options) => {
  let { fileName, url } = (await uploadSingleFile(options.file)) || {}
  if (!fileName) return
  imageUrl.value = url
  // 更新父组件绑定的值
  emit('update:value', imageUrl.value)
}

// 手动删除图片
const handleDelImg = () => {
  imageUrl.value = ''
  // 更新父组件绑定的值
  emit('update:value', imageUrl.value)
}
// 手动清空图片值，用于弹窗关闭时，清空上传组件的图片值
const clearImage = () => {
  imageUrl.value = ''
}
// 监听内容变化，用于编辑回显
watch(
    () => prop.value, () => {
      imageUrl.value = prop.value
    },
    {
      immediate: true,
      deep: true,
    }
)
defineExpose({
  clearImage,
})
</script>
<style scoped lang="scss">
.upload-img-single {
  text-align: var(--align);
  :deep(.el-upload),
  .upload-img-container {
    box-sizing: border-box;
    width: var(--width);
    height: var(--height);
    border-radius: 8px;
    overflow: hidden;
    position: relative;
    background-color: var(--backColor);
    border: 1px dashed #dcdfe6;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .image-wrapper {
    max-width: 100%;
    max-height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .upload-img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    display: block;
    border-radius: 8px;
  }
  :deep(.el-upload) {
    background: var(--backColor);
    border: 1px solid #dcdcdc;
    // &:hover {
    //   border-color: rgba(0, 82, 217, 0.3);
    //   box-shadow: 0 0 0 1px #0052d9 inset !important;
    // }
    .upload-img-container {
      position: relative;
      .del-icon {
        position: absolute;
        top: 5px;
        right: 5px;
        width: 20px;
        opacity: 0;
        border-radius: 0;
      }
      &:hover .del-icon {
        opacity: 1;
      }
    }
    .upload-icon {
      font-size: 16px;
      font-weight: 400;
      color: #999999;
      line-height: 22px;
      text-align: center;
      padding-top: 10px;
      .avatar {
        width: v-bind(avatarWidth);
        margin-bottom: 0px;
      }
      p {
        font-size: v-bind(inTextSize);
      }
    }
  }
  .upload-img-tip {
    color: #999999;
    line-height: 17px;
    font-size: var(--tipSize);
    text-align: left;
    margin-top: 8px;
  }
}
</style>
